import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { forwardRef, useRef, useImperativeHandle, useState } from "react";
import { Button } from "@/components/ui/button";

interface SearchBoxProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
}

export interface SearchBoxRef {
  focus: () => void;
}

export const SearchBox = forwardRef<SearchBoxRef, SearchBoxProps>(({ searchQuery, onSearchChange }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasFocus, setHasFocus] = useState(false);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
  }));

  const handleClearSearch = () => {
    onSearchChange("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="relative">
      <Search
        className={`absolute left-2 top-2.5 h-4 w-4 ${
          hasFocus || searchQuery ? "text-primary" : "text-muted-foreground"
        }`}
      />
      <Input
        ref={inputRef}
        placeholder="Search codes by code or description..."
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
        className="pl-8 pr-8"
        onClick={(e) => e.stopPropagation()}
      />
      {searchQuery && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-1 top-1 h-7 w-7 p-0"
          onClick={handleClearSearch}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Clear search</span>
        </Button>
      )}
    </div>
  );
});

SearchBox.displayName = "SearchBox";
